import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';
import { LandingLayout } from '../landing/shared/LandingLayout';
import { SEOHead } from '../landing/shared/SEOHead';

export default function PrivacyPolicyPage() {
  const { t } = useTranslation('legal');

  const sections = [
    'intro',
    'dataController',
    'dataCollected',
    'howWeUse',
    'thirdParties',
    'googleDataUse',
    'rights',
    'retention',
    'security',
    'changes',
    'contact',
  ] as const;

  return (
    <LandingLayout>
      <SEOHead
        title={t('privacy.title', 'Privacy Policy')}
        description={t('privacy.seoDescription')}
        canonical="https://slotyapp.com/privacy"
      />
      <div className="bg-gray-50 py-12">
        {/* Page Header */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('privacy.title', 'Privacy Policy')}
              </h1>
              <p className="text-sm text-gray-500">
                {t('privacy.lastUpdated', 'Last updated')}: December 2024
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border p-6 sm:p-8 space-y-8">
            {sections.map((section) => (
              <section key={section} className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t(`privacy.sections.${section}.title`)}
                </h2>

                {section === 'dataCollected' ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.intro`)}
                    </p>
                    {['registration', 'booking', 'payment', 'technical'].map((type) => (
                      <div key={type} className="pl-4 border-l-2 border-blue-200">
                        <h3 className="font-medium text-gray-800">
                          {t(`privacy.sections.${section}.${type}.title`)}
                        </h3>
                        <ul className="mt-2 space-y-1 text-gray-600 list-disc list-inside">
                          {(t(`privacy.sections.${section}.${type}.items`, { returnObjects: true }) as string[]).map(
                            (item: string, idx: number) => (
                              <li key={idx}>{item}</li>
                            )
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : section === 'howWeUse' ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.intro`)}
                    </p>
                    <ul className="space-y-2 text-gray-600 list-disc list-inside">
                      {(t(`privacy.sections.${section}.items`, { returnObjects: true }) as string[]).map(
                        (item: string, idx: number) => (
                          <li key={idx}>{item}</li>
                        )
                      )}
                    </ul>
                  </div>
                ) : section === 'thirdParties' ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.intro`)}
                    </p>
                    {['stripe', 'google', 'googleAnalytics', 'googleAds'].map((service) => (
                      <div key={service} className="pl-4 border-l-2 border-blue-200">
                        <h3 className="font-medium text-gray-800">
                          {t(`privacy.sections.${section}.${service}.title`)}
                        </h3>
                        <p className="mt-1 text-gray-600">
                          {t(`privacy.sections.${section}.${service}.description`)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : section === 'googleDataUse' ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.content`)}
                    </p>
                    <ul className="space-y-2 text-gray-600 list-disc list-inside">
                      {(t(`privacy.sections.${section}.items`, { returnObjects: true }) as string[]).map(
                        (item: string, idx: number) => (
                          <li key={idx}>{item}</li>
                        )
                      )}
                    </ul>
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.revocation`)}
                    </p>
                  </div>
                ) : section === 'rights' ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.intro`)}
                    </p>
                    <ul className="space-y-2 text-gray-600 list-disc list-inside">
                      {(t(`privacy.sections.${section}.items`, { returnObjects: true }) as string[]).map(
                        (item: string, idx: number) => (
                          <li key={idx}>{item}</li>
                        )
                      )}
                    </ul>
                    <p className="text-gray-600 font-medium">
                      {t(`privacy.sections.${section}.contact`)}
                    </p>
                  </div>
                ) : section === 'contact' ? (
                  <div className="space-y-2">
                    <p className="text-gray-600">
                      {t(`privacy.sections.${section}.content`)}
                    </p>
                    <p className="text-blue-600">
                      <a href={`mailto:${t(`privacy.sections.${section}.email`)}`}>
                        {t(`privacy.sections.${section}.email`)}
                      </a>
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-600">
                    {t(`privacy.sections.${section}.content`)}
                  </p>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
